/**
 * ProductionProgress — Sprint 5 · Project Symphony
 *
 * Live progress ribbon that sits just below the Executive Header while
 * a campaign is producing or has just completed. Real progress, no fake
 * bars. Uses the polling hook to stay current without flicker.
 */
import { Card } from "@/shared/ui/card";
import { Progress } from "@/shared/ui/progress";
import { Badge } from "@/shared/ui/badge";
import type { GenerationState } from "../types";

export function ProductionProgress({
  state,
  campaignStatusCode,
}: {
  state: GenerationState | undefined;
  campaignStatusCode: string;
}): JSX.Element | null {
  if (!state) return null;
  if (
    campaignStatusCode !== "generating" &&
    campaignStatusCode !== "generation_complete"
  ) {
    return null;
  }

  const { progress_pct, totals } = state;

  return (
    <section data-testid="room-production-progress">
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-3 mb-3">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Campaign Production
          </p>
          <p
            className="text-sm text-muted-foreground"
            data-testid="production-progress-summary"
          >
            {totals.completed} of {totals.total} assets ready
          </p>
          <div className="sm:ml-auto flex flex-wrap gap-2 items-center">
            {totals.generating > 0 && (
              <Badge variant="secondary" data-testid="production-badge-working">
                {totals.generating} in progress
              </Badge>
            )}
            {totals.attention_required > 0 && (
              <Badge variant="destructive" data-testid="production-badge-attention">
                {totals.attention_required} needs attention
              </Badge>
            )}
            {totals.skipped > 0 && (
              <Badge variant="outline" data-testid="production-badge-skipped">
                {totals.skipped} deferred
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-baseline gap-3">
          <span
            className="text-4xl font-semibold tracking-tight tabular-nums"
            data-testid="production-progress-pct"
          >
            {progress_pct}%
          </span>
          <Progress
            value={progress_pct}
            className="flex-1"
            data-testid="production-progress-bar"
          />
        </div>
      </Card>
    </section>
  );
}
